import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../environments/environment';

export interface AdminDashboardStats {
  totalListings: number;
  pendingListings: number;
  totalUsers: number;
  pendingReports: number;
  publishedListings: number;
}

export interface AdminListingView {
  itemId: string;
  name: string;
  url: string;
  status: string;
  statusReason?: string;
  value?: number;
  sellerName?: string;
  sellerUrl?: string;
  createdOn?: string;
  publishedOn?: string;
}

export interface AdminUserView {
  userId: string;
  username: string;
  email: string;
  profileUrl?: string;
  roles: string[];
  planCode?: string;
  activeListings: number;
  maxListings: number;
}

export interface AdminReportView {
  reportId: string;
  reportedBy?: string;
  title?: string;
  text?: string;
  status?: string;
  createdOn?: string;
  itemId?: string;
  itemName?: string;
  itemUrl?: string;
  itemStatus?: string;
  sellerName?: string;
  sellerUrl?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AdminService {

  private baseUrl = '';

  constructor(private http: HttpClient) {
    environment.then((env: any) => {
      this.baseUrl = env.backendURL + '/api/admin';
    });
  }

  private url(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  getDashboard(): Observable<AdminDashboardStats> {
    return this.http.get<AdminDashboardStats>(this.url('/dashboard'));
  }

  getListings(status: string = 'PENDING'): Observable<AdminListingView[]> {
    const params = new HttpParams().set('status', status);
    return this.http.get<AdminListingView[]>(this.url('/listings'), {params});
  }

  updateListingStatus(itemId: string, status: string, reason?: string): Observable<AdminListingView> {
    return this.http.put<AdminListingView>(this.url(`/listings/${itemId}/status`), {status, reason});
  }

  getUsers(): Observable<AdminUserView[]> {
    return this.http.get<AdminUserView[]>(this.url('/users'));
  }

  getPendingReports(): Observable<AdminReportView[]> {
    return this.http.get<AdminReportView[]>(this.url('/reports/pending'));
  }

  getOpenListingReports(itemId: string): Observable<AdminReportView[]> {
    return this.http.get<AdminReportView[]>(this.url(`/reports/listing/${itemId}`));
  }

  updateReportStatus(reportId: string, status: string, options?: {takedown?: boolean; reason?: string}): Observable<AdminReportView> {
    return this.http.put<AdminReportView>(this.url(`/reports/${reportId}/status`), {
      status,
      takedown: options?.takedown ? 'true' : 'false',
      reason: options?.reason || ''
    });
  }

  takedownFromReport(reportId: string, reason?: string): Observable<AdminReportView> {
    return this.http.post<AdminReportView>(this.url(`/reports/${reportId}/takedown`), {reason, takedown: true, status: 'resolved'});
  }
}
